import { createApp } from "vue";
import VideoCarousel from "./components/VideoCarousel.vue";
import VideoAnalytics from "./analytics.js";
import carouselStyles from "./styles/carousel.css";
import widgetStyles from "./styles/widget.css";

class VideoCarouselWidget {
    constructor(options = {}) {
        this.options = {
            apiKey: options.apiKey || "",
            apiUrl: options.apiUrl || "https://api.serviceiro.com.br/embed/videos",
            containerId: options.containerId || null,
            itemsVisible: options.itemsVisible || 5,
            autoAdvanceInterval: options.autoAdvanceInterval || 5000,
            autoplay: options.autoplay !== false,
            muted: options.muted !== false,
            devMode: options.devMode || process.env.NODE_ENV === "development",
            analytics: options.analytics || null,
            ...options,
        };

        this.app = null;
        this.stories = [];
        this.isLoaded = false;
        this.container = null;
        this.shadowRoot = null;
        this.widgetInstance = null;
        this.analytics = null;

        if (this.options.analytics) {
            this.analytics = new VideoAnalytics(
                this.options.analytics.measurementId,
                this.options.analytics.options || {},
                this.options.apiKey
            );
        }

        this.init();
    }

    async init() {
        try {
            if (!this.options.apiKey && !this.options.devMode) {
                throw new Error("API key is required");
            }

            if (!this.options.containerId) {
                throw new Error("containerId is required for carousel format");
            }

            await this.loadStories();
            this.createContainer();
            this.mountWidget();

            this.isLoaded = true;
            this.dispatchEvent("carousel-loaded");

        } catch (error) {
            console.error("VideoCarouselWidget Error:", error);
            if (this.options.devMode || this.options.apiKey === "test-key") {
                this.stories = this.getMockStories();
                this.createContainer();
                this.mountWidget();
                this.isLoaded = true;
                this.dispatchEvent("carousel-loaded");
            } else {
                this.dispatchEvent("carousel-error", { error: error.message });
            }
        }
    }

    async loadStories() {
        if (this.options.devMode || this.options.apiKey === "test-key") {
            this.stories = this.getMockStories();
            return;
        }
        try {
            const currentUrl = window.location.href;
            const response = await fetch(`${this.options.apiUrl}?url=${encodeURIComponent(currentUrl)}`, {
                headers: {
                    Authorization: `Bearer ${this.options.apiKey}`,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            const storiesArray = Array.isArray(data) ? data : (Array.isArray(data.videos) ? data.videos : []);
            this.stories = this.normalizeStories(storiesArray);
        } catch (error) {
            console.error("Failed to load stories:", error);
            throw error;
        }
    }

    normalizeStories(rawStories) {
        return rawStories.map(story => ({
            id: story.id,
            name: story.title || story.name,
            description: story.description,
            videos: JSON.parse(story.videos).map(video => ({
                url: video.url || video.src || video.attachment,
                thumbnail: video.thumbnail || video.poster || video.thumb || "",
                poster: video.poster || video.thumbnail || ""
            }))
        }));
    }

    getMockStories() {
        return [
            { id: 1, name: "For Bigger Blazes", videos: [{ url: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4", thumbnail: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/images/ForBiggerBlazes.jpg" }] },
            { id: 2, name: "Big Buck Bunny", videos: [{ url: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4", thumbnail: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/images/BigBuckBunny.jpg" }] },
            { id: 3, name: "Elephants Dream", videos: [{ url: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4", thumbnail: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/images/ElephantsDream.jpg" }] },
            { id: 4, name: "For Bigger Blazes", videos: [{ url: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4", thumbnail: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/images/ForBiggerBlazes.jpg" }] },
            { id: 5, name: "Big Buck Bunny", videos: [{ url: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4", thumbnail: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/images/BigBuckBunny.jpg" }] },
            { id: 6, name: "Big Buck Bunny", videos: [{ url: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4", thumbnail: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/images/BigBuckBunny.jpg" }] }
        ];
    }

    createContainer() {
        this.container = document.getElementById(this.options.containerId);
        if (!this.container) throw new Error(`Container #${this.options.containerId} not found`);

        this.shadowRoot = this.container.shadowRoot || this.container.attachShadow({ mode: "open" });

        let appContainer = this.shadowRoot.querySelector(".video-carousel-widget-host");
        if (!appContainer) {
            appContainer = document.createElement("div");
            appContainer.className = "video-carousel-widget-host";
            this.injectStyles();
            this.shadowRoot.appendChild(appContainer);
        }
    }

    injectStyles() {
        const styleEl = document.createElement("style");
        styleEl.textContent = carouselStyles + "\n" + widgetStyles;
        this.shadowRoot.appendChild(styleEl);
    }

    mountWidget() {
        const appContainer = this.shadowRoot.querySelector(".video-carousel-widget-host");
        this.app = createApp(VideoCarousel, {
            stories: this.stories,
            analytics: this.analytics,
            ...this.options
        });
        this.widgetInstance = this.app.mount(appContainer);
    }

    dispatchEvent(eventName, detail = {}) {
        const event = new CustomEvent(`videostories-carousel:${eventName}`, {
            detail: { widget: this, version: "2.0.0", ...detail },
            bubbles: true,
            composed: true,
        });
        if (this.container) this.container.dispatchEvent(event);
        window.dispatchEvent(event);
    }

    refresh() { this.init(); return this; }
    destroy() { if (this.app) this.app.unmount(); return this; }
}

if (typeof window !== "undefined") {
    window.VideoCarouselWidget = VideoCarouselWidget;
    // Auto-init for carousel
    document.addEventListener("DOMContentLoaded", () => {
        const scripts = document.querySelectorAll('script[src*="video-carousel-widget"][data-api-key]');
        scripts.forEach(script => {
            const options = {
                apiKey: script.dataset.apiKey,
                containerId: script.dataset.containerId,
                itemsVisible: script.dataset.itemsVisible ? parseInt(script.dataset.itemsVisible) : 5,
                autoAdvanceInterval: script.dataset.autoAdvanceInterval ? parseInt(script.dataset.autoAdvanceInterval) : 5000,
                devMode: script.dataset.devMode === "true"
            };
            new VideoCarouselWidget(options);
        });
    });
}

export default VideoCarouselWidget;